import { Input as ShadcnInput } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/domains/shared/services/cn";
import type { LucideIcon } from "lucide-react";
import * as React from "react";

export type InputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, "size" | "icon"> & {
    isDisabled?: boolean;
    isInvalid?: boolean;
    isRequired?: boolean;
    isReadOnly?: boolean;
    size?: "sm" | "md" | "lg";
    label?: React.ReactNode;
    hint?: React.ReactNode;
    hideRequiredIndicator?: boolean;
    inputClassName?: string;
    icon?: LucideIcon;
    "data-testid"?: string;
};

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
    (
        {
            className,
            disabled,
            isDisabled,
            size = "sm",
            required,
            isRequired,
            readOnly,
            isReadOnly,
            isInvalid,
            label,
            hint,
            hideRequiredIndicator,
            inputClassName,
            icon: Icon,
            id,
            "aria-describedby": ariaDescribedBy,
            "aria-invalid": ariaInvalid,
            "data-testid": dataTestId,
            ...props
        },
        ref,
    ) => {
        const generatedId = React.useId();
        const inputId = id ?? generatedId;
        const hintId = hint ? `${inputId}-hint` : undefined;
        const resolvedRequired = required ?? isRequired;

        return (
            <div className={cn("flex w-full flex-col gap-1.5", className)} data-testid={dataTestId}>
                {label && (
                    <Label htmlFor={inputId} data-label="">
                        {label}
                        {resolvedRequired && !hideRequiredIndicator ? (
                            <span aria-hidden="true" className="text-destructive">
                                *
                            </span>
                        ) : null}
                    </Label>
                )}
                <div className="relative">
                    {Icon ? (
                        <Icon
                            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
                            aria-hidden
                        />
                    ) : null}
                    <ShadcnInput
                        ref={ref}
                        id={inputId}
                        disabled={disabled ?? isDisabled}
                        required={resolvedRequired}
                        readOnly={readOnly ?? isReadOnly}
                        aria-readonly={readOnly ?? isReadOnly ? true : undefined}
                        aria-invalid={isInvalid || ariaInvalid || undefined}
                        aria-describedby={[ariaDescribedBy, hintId].filter(Boolean).join(" ") || undefined}
                        className={cn(
                            size === "md" && "h-8 px-3",
                            size === "lg" && "h-9 px-3.5",
                            Icon && "pl-9",
                            inputClassName,
                        )}
                        {...props}
                    />
                </div>
                {hint ? (
                    <span id={hintId} className={cn("text-sm", isInvalid ? "text-destructive" : "text-muted-foreground")}>
                        {hint}
                    </span>
                ) : null}
            </div>
        );
    },
);
Input.displayName = "Input";
